import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmtTL, ORDER_STATUS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";

const COLORS = ["#D96B27", "#2D4A3E", "#E5A93C", "#557A68", "#1E5276", "#A62B2B"];

export default function Reports() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const isBuyer = user?.role !== "supplier";

    useEffect(() => {
        api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Raporlar & Analitik</h1>
                <p className="mt-1 text-sm text-muted-foreground">{isBuyer ? "Satınalma performansınız" : "Satış ve operasyon performansınız"}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="report-kpis">
                {[
                    { label: isBuyer ? "Toplam Harcama" : "Toplam Ciro", value: fmtTL(data.kpis.total_spend), tid: "report-total" },
                    { label: "Sipariş Sayısı", value: data.kpis.order_count, tid: "report-orders" },
                    { label: isBuyer ? "Tahmini Tasarruf" : "Tahsilat Bekleyen", value: fmtTL(isBuyer ? data.kpis.savings : data.kpis.unpaid_total), tid: "report-savings" },
                    { label: "Açık Sipariş", value: data.kpis.open_orders, tid: "report-open" },
                ].map((k) => (
                    <div key={k.tid} className="rounded-2xl border bg-card p-6 shadow-sm" data-testid={k.tid}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k.label}</p>
                        <p className="mt-2 font-display text-2xl font-extrabold">{k.value}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-7" data-testid="report-monthly">
                    <h2 className="font-display text-lg font-bold">Aylık {isBuyer ? "Harcama" : "Satış"} Trendi</h2>
                    <div className="mt-4">
                        {data.monthly.length ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={data.monthly}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD3" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#68615A" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="#68615A" tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5DCD3" }} />
                                    <Bar dataKey="spend" name="Tutar" fill="#D96B27" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <p className="py-16 text-center text-sm text-muted-foreground">Veri yok.</p>}
                    </div>
                </div>

                <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-5" data-testid="report-categories">
                    <h2 className="font-display text-lg font-bold">Kategori Dağılımı</h2>
                    <div className="mt-4">
                        {data.by_category.length ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={data.by_category} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                                        {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5DCD3" }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <p className="py-16 text-center text-sm text-muted-foreground">Veri yok.</p>}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm lg:col-span-7" data-testid="report-suppliers">
                    <div className="border-b bg-muted/50 px-5 py-3"><h2 className="font-display font-bold">Tedarikçi Bazlı Dağılım</h2></div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-4">Tedarikçi</th>
                                <th className="p-4">Sipariş</th>
                                <th className="p-4 text-right">Tutar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.by_supplier.map((s, i) => (
                                <tr key={s.name} className="border-b last:border-0" data-testid={`report-supplier-${i}`}>
                                    <td className="p-4 font-semibold">{s.name}</td>
                                    <td className="p-4 text-muted-foreground">{s.orders}</td>
                                    <td className="p-4 text-right font-display font-extrabold text-terra-600">{fmtTL(s.spend)}</td>
                                </tr>
                            ))}
                            {!data.by_supplier.length && <tr><td colSpan={3} className="p-10 text-center text-sm text-muted-foreground">Veri yok.</td></tr>}
                        </tbody>
                    </table>
                </div>

                <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-5" data-testid="report-status-dist">
                    <h2 className="font-display text-lg font-bold">Sipariş Durum Dağılımı</h2>
                    <div className="mt-4 space-y-3">
                        {data.status_dist.map((s) => (
                            <div key={s.status} className="flex items-center justify-between rounded-xl border p-3" data-testid={`status-dist-${s.status}`}>
                                <Badge variant="outline" className={ORDER_STATUS[s.status]?.cls}>{ORDER_STATUS[s.status]?.label}</Badge>
                                <span className="font-display text-lg font-extrabold">{s.count}</span>
                            </div>
                        ))}
                        {!data.status_dist.length && <p className="py-10 text-center text-sm text-muted-foreground">Veri yok.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}
